import React from 'react';
import { WebsiteStatus, type WebsiteReadResponse } from 'src/api/generated';
import {
  DescriptionList,
  type DescriptionListItem,
} from 'src/modules/shared/components/molecules/description-list/description-list';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import {
  WEBSITE_DEPLOYMENT_STATUS_LABEL_KEYS,
  WEBSITE_STATUS_LABEL_KEYS,
} from '../../utils/website-status-labels.util';

/** Props for the publication summary. */
interface WebsiteStatusSummaryProps {
  /** Current website, when one exists. */
  website?: WebsiteReadResponse | null;
  /** Public URL of the live site, when it is published. */
  liveSiteUrl: string | null;
  /** Provisional address of the site. */
  provisionalUrl: string;
}

/**
 * Publication state as label and value pairs: status, live and provisional
 * addresses, primary domain and the latest publication.
 *
 * @param props - Website and its addresses.
 * @returns The summary list.
 */
export const WebsiteStatusSummary: React.FC<WebsiteStatusSummaryProps> = ({
  website,
  liveSiteUrl,
  provisionalUrl,
}) => {
  const { t } = useI18n();
  const latestDeployment = website?.deployments?.[0] ?? null;
  const rows: DescriptionListItem[] = [
    {
      label: t('websites.settings.status.labels.status'),
      value: t(
        WEBSITE_STATUS_LABEL_KEYS[website?.status ?? WebsiteStatus.DRAFT]
      ),
    },
    {
      label: t('websites.settings.status.labels.liveUrl'),
      value: liveSiteUrl ?? t('websites.settings.status.pendingFirstDeploy'),
    },
    {
      label: t('websites.settings.status.labels.provisionalUrl'),
      value: provisionalUrl,
    },
  ];

  if (website?.primaryDomain) {
    rows.push({
      label: t('websites.settings.status.labels.primaryDomain'),
      value: `https://${website.primaryDomain}`,
    });
  }
  if (latestDeployment) {
    rows.push({
      label: t('websites.settings.status.labels.latestDeployment'),
      value: t('websites.settings.status.deploymentSummary', {
        version: latestDeployment.deploymentVersion,
        status: t(
          WEBSITE_DEPLOYMENT_STATUS_LABEL_KEYS[latestDeployment.status]
        ),
      }),
    });
  }
  if (latestDeployment?.errorMessage) {
    rows.push({
      label: t('websites.settings.status.labels.deploymentError'),
      value: latestDeployment.errorMessage,
    });
  }

  return <DescriptionList items={rows} />;
};
